import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import IdeaCard from "@/components/ideas/IdeaCard";
import SponsorSlot from "@/components/sponsors/SponsorSlot";
import NewIdeaDialog from "@/components/ideas/NewIdeaDialog";
import SetupChecklist from "@/components/onboarding/SetupChecklist";
import { Button } from "@/components/ui/button";
import { Plus, Lightbulb, Sparkles, Star, Flame, Trophy, Users, Hash, X } from "lucide-react";

const CATEGORIES = ["All", "Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];
const CATEGORY_TO_SLUG: Record<string, string> = Object.fromEntries(
  CATEGORIES.filter((c) => c !== "All").map((c) => [c.toLowerCase(), c])
);

type SortKey = "fresh" | "top_week" | "top_all" | "following";

const SORTS: { key: SortKey; label: string; icon: typeof Flame; authOnly?: boolean }[] = [
  { key: "fresh", label: "Fresh", icon: Flame },
  { key: "top_week", label: "Top this week", icon: Sparkles },
  { key: "top_all", label: "Top all-time", icon: Trophy },
  { key: "following", label: "Following", icon: Users, authOnly: true },
];

const IdeasBoard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string; tag?: string }>();
  const queryClient = useQueryClient();

  // Derive category/tag from URL params (e.g. /ideas/category/housing or /ideas/tag/dbe)
  const urlCategory = params.slug ? CATEGORY_TO_SLUG[params.slug.toLowerCase()] ?? "All" : null;
  const urlTag = params.tag ? decodeURIComponent(params.tag) : null;

  const [selectedCategory, setSelectedCategory] = useState(urlCategory ?? "All");
  const [sort, setSort] = useState<SortKey>("fresh");
  const [grantReadyOnly, setGrantReadyOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"all" | "concept" | "project">("all");
  const [newIdeaOpen, setNewIdeaOpen] = useState(false);

  // Keep state in sync if the route changes
  useEffect(() => {
    if (urlCategory) setSelectedCategory(urlCategory);
  }, [urlCategory]);

  // Auto-open NewIdeaDialog when arriving from /welcome with ?new=1
  useEffect(() => {
    if (!user) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("new") === "1") {
      setNewIdeaOpen(true);
      url.searchParams.delete("new");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [user]);

  // Following list (used for sort + filter)
  const { data: followedIds = [] } = useQuery({
    queryKey: ["profile-following", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_followers")
        .select("following_user_id")
        .eq("follower_user_id", user!.id);
      if (error) throw error;
      return data.map((r) => r.following_user_id);
    },
    enabled: !!user,
  });

  // Top voted ideas this week — IDs only, used to sort
  const { data: weeklyTopIds = [] } = useQuery({
    queryKey: ["weekly-top-vote-ids"],
    queryFn: async () => {
      const sinceIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("idea_votes")
        .select("idea_id, created_at")
        .gte("created_at", sinceIso);
      if (error) throw error;
      const counts = new Map<string, number>();
      for (const v of data) counts.set(v.idea_id, (counts.get(v.idea_id) ?? 0) + 1);
      return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
    },
    staleTime: 60_000,
  });

  const { data: ideas = [], isLoading } = useQuery({
    queryKey: ["ideas", selectedCategory, typeFilter, urlTag],
    queryFn: async () => {
      let query = supabase
        .from("ideas")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: false });

      if (selectedCategory !== "All") {
        query = query.eq("category", selectedCategory);
      }
      if (typeFilter !== "all") {
        query = query.eq("idea_type", typeFilter);
      }
      if (urlTag) {
        query = query.contains("tags", [urlTag]);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch profiles for all unique user_ids
      const userIds = [...new Set(data.map((i) => i.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.user_id, p])
      );

      return data.map((idea) => ({
        ...idea,
        profiles: profileMap.get(idea.user_id) || null,
      }));
    },
  });

  const { data: userVotes = [] } = useQuery({
    queryKey: ["user-votes", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_votes")
        .select("idea_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data.map((v) => v.idea_id);
    },
    enabled: !!user,
  });

  // Authenticated-only: which ideas have a grant-ready brief
  const { data: grantReadyIds = [] } = useQuery({
    queryKey: ["grant-ready-set"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_grant_details")
        .select("idea_id");
      if (error) throw error;
      return data.map((r) => r.idea_id);
    },
    enabled: !!user,
  });

  const grantReadySet = new Set<string>(grantReadyIds);
  const followedSet = useMemo(() => new Set(followedIds), [followedIds]);

  // Apply secondary filters + sort
  const sortedIdeas = useMemo(() => {
    let list = [...ideas];
    if (grantReadyOnly) list = list.filter((i: any) => grantReadySet.has(i.id));
    if (sort === "following") {
      list = list.filter((i: any) => i.user_id && followedSet.has(i.user_id));
    }

    const now = Date.now();
    const isBoosted = (i: any) =>
      i.boosted_until && new Date(i.boosted_until).getTime() > now;

    if (sort === "fresh") {
      list.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    } else if (sort === "top_all") {
      list.sort((a, b) => (b.vote_count ?? 0) - (a.vote_count ?? 0));
    } else if (sort === "top_week") {
      const rank = new Map(weeklyTopIds.map((id, idx) => [id, idx]));
      list.sort((a, b) => {
        const ra = rank.has(a.id) ? rank.get(a.id)! : Infinity;
        const rb = rank.has(b.id) ? rank.get(b.id)! : Infinity;
        if (ra !== rb) return ra - rb;
        return (b.vote_count ?? 0) - (a.vote_count ?? 0);
      });
    } else if (sort === "following") {
      list.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    }

    // Boosted always floats to top of current sort
    list.sort((a, b) => Number(isBoosted(b)) - Number(isBoosted(a)));
    return list;
  }, [ideas, grantReadyOnly, grantReadySet, sort, followedSet, weeklyTopIds]);

  // Featured / Editor's Picks lane — independent of filters/sort
  const { data: featuredIdeas = [] } = useQuery({
    queryKey: ["featured-ideas"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("status", "published")
        .eq("is_featured", true)
        .order("featured_at", { ascending: false, nullsFirst: false })
        .limit(6);
      if (error) throw error;
      const userIds = [...new Set(data.map((i) => i.user_id))];
      if (userIds.length === 0) return data.map((i) => ({ ...i, profiles: null }));
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);
      const map = new Map((profiles || []).map((p) => [p.user_id, p]));
      return data.map((i) => ({ ...i, profiles: map.get(i.user_id) || null }));
    },
  });

  const voteMutation = useMutation({
    mutationFn: async ({ ideaId, hasVoted }: { ideaId: string; hasVoted: boolean }) => {
      if (!user) {
        navigate("/auth");
        return;
      }
      if (hasVoted) {
        const { error } = await supabase
          .from("idea_votes")
          .delete()
          .eq("idea_id", ideaId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("idea_votes")
          .insert({ idea_id: ideaId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ideas"] });
      queryClient.invalidateQueries({ queryKey: ["user-votes"] });
      queryClient.invalidateQueries({ queryKey: ["weekly-top-vote-ids"] });
      queryClient.invalidateQueries({ queryKey: ["featured-ideas"] });
    },
  });

  const handleNewIdea = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setNewIdeaOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const handleCategoryClick = (cat: string) => {
    setSelectedCategory(cat);
    if (cat === "All") {
      navigate("/ideas");
    } else {
      navigate(`/ideas/category/${cat.toLowerCase()}`);
    }
  };

  const showFeaturedLane =
    !urlTag && selectedCategory === "All" && sort !== "following" && featuredIdeas.length > 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6">
          <SetupChecklist variant="inline" className="mb-6" />
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3">
                <Lightbulb className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold">
                  {urlTag
                    ? `#${urlTag}`
                    : selectedCategory !== "All"
                    ? `${selectedCategory} ideas`
                    : "Actionable Ideas"}
                </h1>
              </div>
              <p className="text-muted-foreground mt-1">
                {urlTag
                  ? `Ideas tagged with #${urlTag}.`
                  : selectedCategory !== "All"
                  ? `Curated ${selectedCategory.toLowerCase()} ideas from Alabama communities.`
                  : "Actionable ideas from Alabama communities. Upvote what matters."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleNewIdea} className="gap-2">
                <Plus className="h-4 w-4" />
                Share an Idea
              </Button>
            </div>
          </div>

          {/* Tag breadcrumb */}
          {urlTag && (
            <div className="mb-6 flex items-center gap-2 text-sm">
              <Hash className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Filtered by tag</span>
              <button
                onClick={() => navigate("/ideas")}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-colors"
              >
                {urlTag} <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Sort tabs */}
          <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-border">
            {SORTS.map((s) => {
              const Icon = s.icon;
              if (s.authOnly && !user) return null;
              const active = sort === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSort(s.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    active
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Featured / Editor's Picks lane */}
          {showFeaturedLane && (
            <section className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                    Editor's Picks
                  </h2>
                </div>
                <span className="text-xs text-muted-foreground">Curated by emrkt</span>
              </div>
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
                {featuredIdeas.map((idea: any) => (
                  <IdeaCard
                    key={`featured-${idea.id}`}
                    idea={idea}
                    hasVoted={userVotes.includes(idea.id)}
                    onVote={(hasVoted) => voteMutation.mutate({ ideaId: idea.id, hasVoted })}
                    currentUserId={user?.id ?? null}
                    isGrantReady={grantReadySet.has(idea.id)}
                  />
                ))}
              </div>
            </section>
          )}

          <div className="mb-8">
            <SponsorSlot placement="ideas_sidebar" />
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {(["all", "concept", "project"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                  typeFilter === t
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent"
                }`}
              >
                {t === "all" ? "All types" : `${t}s`}
              </button>
            ))}
            <span className="w-px h-6 bg-border self-center mx-1" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                {cat}
              </button>
            ))}
            {user && (
              <button
                onClick={() => setGrantReadyOnly((v) => !v)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  grantReadyOnly
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Grant-Ready
              </button>
            )}
          </div>

          {/* Masonry grid */}
          {isLoading ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl h-64 mb-4 animate-pulse break-inside-avoid" />
              ))}
            </div>
          ) : sortedIdeas.length === 0 ? (
            <div className="text-center py-20">
              <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {sort === "following"
                  ? "Nothing from people you follow yet"
                  : grantReadyOnly
                  ? "No grant-ready ideas yet"
                  : "No ideas yet"}
              </h2>
              <p className="text-muted-foreground mb-6">
                {sort === "following"
                  ? "Follow a community member from their profile to see their work here."
                  : grantReadyOnly
                  ? "Be the first to add a grant-ready brief to an idea."
                  : "Be the first to share an actionable idea!"}
              </p>
              {sort === "following" ? (
                <Button asChild variant="outline" className="gap-2">
                  <Link to="/ideas" onClick={() => setSort("fresh")}>
                    Browse Fresh ideas
                  </Link>
                </Button>
              ) : (
                <Button onClick={handleNewIdea} className="gap-2">
                  <Plus className="h-4 w-4" /> Share an Idea
                </Button>
              )}
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {sortedIdeas.map((idea: any) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  hasVoted={userVotes.includes(idea.id)}
                  onVote={(hasVoted) => voteMutation.mutate({ ideaId: idea.id, hasVoted })}
                  currentUserId={user?.id ?? null}
                  isGrantReady={grantReadySet.has(idea.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      {user && <NewIdeaDialog open={newIdeaOpen} onOpenChange={setNewIdeaOpen} />}
    </div>
  );
};

export default IdeasBoard;
